// Custom Assistant Creator Modal
import { useState, useEffect } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import axios from "axios";
import { Plus, Trash2, Edit2, User, UserCircle, Bot, Loader2, Sparkles } from "lucide-react";
import { useLanguage } from "@/i18n";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const VOICE_OPTIONS = [
  { value: "onyx", label: "Onyx (Erkek - Derin)", gender: "male" },
  { value: "nova", label: "Nova (Kadın - Sıcak)", gender: "female" },
  { value: "alloy", label: "Alloy (Nötr)", gender: "neutral" },
  { value: "echo", label: "Echo (Erkek - Derin)", gender: "male" },
  { value: "shimmer", label: "Shimmer (Kadın - Yumuşak)", gender: "female" },
  { value: "fable", label: "Fable (Nötr - Hikaye)", gender: "neutral" },
];

const GENDER_OPTIONS = [
  { value: "male", label: "Erkek", labelKey: "gender_male", icon: User },
  { value: "female", label: "Kadın", labelKey: "gender_female", icon: UserCircle },
  { value: "neutral", label: "Nötr", labelKey: "gender_neutral", icon: Bot },
];

const PERSONALITY_STYLES = [
  { 
    value: "formal", 
    label: "Saygılı/Resmi",
    labelKey: "style_formal",
    description: '"Siz" ile hitap, profesyonel dil',
    descKey: "style_formal_desc",
    promptAddition: `\n\nKONUŞMA TARZI:
- Her zaman "siz" ile hitap et
- Profesyonel ve saygılı bir dil kullan
- Resmi ama sıcak ol
- Emoji kullanma veya çok az kullan`
  },
  { 
    value: "normal", 
    label: "Normal Samimi",
    labelKey: "style_normal",
    description: '"Sen" ile hitap ama aşırı samimi değil',
    descKey: "style_normal_desc",
    promptAddition: `\n\nKONUŞMA TARZI:
- "Sen" ile hitap et ama aşırı samimi olma
- Doğal ve rahat bir dil kullan
- Abartılı samimi kelimeler kullanma (kanka, canım vb.)
- Emoji kullanımını dengeli tut`
  },
  { 
    value: "adaptive", 
    label: "Kullanıcıya Uyumlu",
    labelKey: "style_adaptive",
    description: 'Kullanıcının tarzına göre uyum sağlar',
    descKey: "style_adaptive_desc",
    promptAddition: `\n\nKONUŞMA TARZI UYUMU:
- Kullanıcının üslubuna uyum sağla
- Kullanıcı samimi konuşursa sen de samimi ol
- Kullanıcı resmi konuşursa sen de saygılı ol
- İlk mesajda nötr başla, kullanıcının tarzını gözlemle
- Emoji kullanımını kullanıcıya göre ayarla`
  },
  { 
    value: "friendly", 
    label: "Çok Samimi",
    labelKey: "style_friendly",
    description: 'Kanka, canım gibi samimi hitaplar',
    descKey: "style_friendly_desc",
    promptAddition: `\n\nKONUŞMA TARZI:
- Çok samimi ve arkadaşça konuş
- "Kanka", "dostum", "canım" gibi samimi hitaplar kullan
- Emoji kullanmaktan çekinme
- Esprili ve rahat ol`
  }
];

const SAMPLE_PROMPTS = [
  {
    name: "Koç",
    description: "Motivasyon ve spor koçu",
    prompt: "Sen motive edici bir koçsun. Kullanıcıyı hedeflerine ulaşması için cesaretlendir. Spor, sağlık ve kişisel gelişim konularında tavsiyelerde bulun. Enerjik ve pozitif ol.",
    gender: "male",
    voice: "onyx",
    personality_style: "friendly"
  },
  {
    name: "Şef",
    description: "Yemek ve mutfak uzmanı",
    prompt: "Sen yetenekli bir şef ve yemek uzmanısın. Tarifler, pişirme teknikleri ve malzeme seçimi konusunda yardımcı ol. Türk mutfağı ve dünya mutfaklarını iyi bilirsin.",
    gender: "neutral",
    voice: "alloy",
    personality_style: "normal"
  },
  {
    name: "Terapist",
    description: "Anlayışlı dinleyici",
    prompt: "Sen empatik bir terapistsin. Kullanıcının duygularını anla ve destekle. Yargılamadan dinle, açık uçlu sorular sor ve olumlu bir yaklaşım sergile. Profesyonel ama sıcak ol.",
    gender: "female",
    voice: "nova",
    personality_style: "formal"
  }
];

export function AssistantCreatorModal({ open, onOpenChange, onAssistantCreated, onAssistantDeleted }) {
  const { t } = useLanguage();
  const [assistants, setAssistants] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isCreating, setIsCreating] = useState(false);
  const [editingAssistant, setEditingAssistant] = useState(null);
  const [showForm, setShowForm] = useState(false);
  
  // Form state
  const [formData, setFormData] = useState({
    name: "",
    description: "",
    system_prompt: "",
    gender: "neutral",
    voice: "alloy",
    personality_style: "adaptive"
  });

  useEffect(() => {
    if (open) {
      loadAssistants();
    }
  }, [open]);

  const loadAssistants = async () => {
    setIsLoading(true);
    try {
      const res = await axios.get(`${API}/assistants`);
      setAssistants(res.data.assistants || []);
    } catch (error) {
      if (error.response?.status !== 401) {
        toast.error(t('error_occurred'));
      }
    } finally {
      setIsLoading(false);
    }
  };

  const resetForm = () => {
    setFormData({
      name: "",
      description: "",
      system_prompt: "",
      gender: "neutral",
      voice: "alloy",
      personality_style: "adaptive"
    });
    setEditingAssistant(null);
    setShowForm(false);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsCreating(true);
    
    // Add personality style to system prompt
    const selectedStyle = PERSONALITY_STYLES.find(s => s.value === formData.personality_style);
    const finalPrompt = formData.system_prompt + (selectedStyle?.promptAddition || "");
    
    const dataToSend = {
      ...formData,
      system_prompt: finalPrompt
    };
    
    try {
      if (editingAssistant) {
        // Update existing
        const res = await axios.put(`${API}/assistants/${editingAssistant.id}`, dataToSend);
        toast.success(res.data.message || t('saved_successfully'));
        loadAssistants();
      } else {
        // Create new
        const res = await axios.post(`${API}/assistants`, dataToSend);
        loadAssistants();
        if (onAssistantCreated) {
          onAssistantCreated(res.data.assistant);
        }
      }
      resetForm();
    } catch (error) {
      toast.error(error.response?.data?.detail || t('error_occurred'));
    } finally {
      setIsCreating(false);
    }
  };

  const handleDelete = async (assistant) => {
    if (!window.confirm(t('confirm_delete'))) {
      return;
    }
    
    try {
      await axios.delete(`${API}/assistants/${assistant.id}`);
      loadAssistants();
      if (onAssistantDeleted) {
        onAssistantDeleted(assistant.id);
      }
    } catch (error) {
      toast.error(t('error_occurred'));
    }
  };

  const handleEdit = (assistant) => {
    setFormData({
      name: assistant.name,
      description: assistant.description,
      system_prompt: assistant.system_prompt,
      gender: assistant.gender,
      voice: assistant.voice,
      personality_style: assistant.personality_style || "adaptive"
    });
    setEditingAssistant(assistant);
    setShowForm(true);
  };

  const applySamplePrompt = (sample) => {
    setFormData({
      name: sample.name,
      description: sample.description,
      system_prompt: sample.prompt,
      gender: sample.gender,
      voice: sample.voice,
      personality_style: sample.personality_style || "adaptive"
    });
  };

  const GenderIcon = GENDER_OPTIONS.find(g => g.value === formData.gender)?.icon || Bot;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-zinc-900 border-zinc-800 text-white max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="text-xl font-bold flex items-center gap-2">
            <Sparkles className="text-purple-400" size={24} />
            {t('create_assistant')}
          </DialogTitle>
          <DialogDescription className="text-zinc-400">
            {t('create_assistant')} (max. 5)
          </DialogDescription>
        </DialogHeader>

        {!showForm ? (
          <div className="space-y-4">
            {/* Existing Assistants List */}
            {isLoading ? (
              <div className="flex items-center justify-center py-8">
                <Loader2 className="animate-spin text-blue-500" size={32} />
              </div>
            ) : (
              <>
                {assistants.length > 0 && (
                  <div className="space-y-2">
                    <h3 className="text-sm font-medium text-zinc-400">{t('create_assistant')} ({assistants.length}/5)</h3>
                    <div className="grid gap-2">
                      {assistants.map((assistant) => {
                        const Icon = GENDER_OPTIONS.find(g => g.value === assistant.gender)?.icon || Bot;
                        return (
                          <div
                            key={assistant.id}
                            className="flex items-center justify-between p-3 bg-zinc-800 rounded-lg border border-zinc-700"
                          >
                            <div className="flex items-center gap-3">
                              <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${
                                assistant.gender === "male" ? "bg-blue-600" :
                                assistant.gender === "female" ? "bg-pink-600" : "bg-purple-600"
                              }`}>
                                <Icon size={20} className="text-white" />
                              </div>
                              <div>
                                <h4 className="font-medium">{assistant.name}</h4>
                                <p className="text-xs text-zinc-500">{assistant.description}</p>
                              </div>
                            </div>
                            <div className="flex gap-1">
                              <Button
                                variant="ghost"
                                size="icon"
                                className="h-8 w-8 text-zinc-400 hover:text-blue-400"
                                onClick={() => handleEdit(assistant)}
                              >
                                <Edit2 size={16} />
                              </Button>
                              <Button
                                variant="ghost"
                                size="icon"
                                className="h-8 w-8 text-zinc-400 hover:text-red-400"
                                onClick={() => handleDelete(assistant)}
                              >
                                <Trash2 size={16} />
                              </Button>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}

                {assistants.length < 5 && (
                  <Button
                    onClick={() => setShowForm(true)}
                    className="w-full bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700"
                  >
                    <Plus size={18} className="mr-2" />
                    {t('create_assistant')}
                  </Button>
                )}

                {assistants.length === 0 && (
                  <div className="text-center py-4 text-zinc-500">
                    <Bot size={48} className="mx-auto mb-2 opacity-30" />
                    <p>{t('no_results')}</p>
                  </div>
                )}
              </>
            )}
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="space-y-4">
            {/* Sample Prompts */}
            {!editingAssistant && (
              <div className="space-y-2">
                <Label className="text-sm text-zinc-400">{t('create_assistant')}</Label>
                <div className="flex gap-2 flex-wrap">
                  {SAMPLE_PROMPTS.map((sample) => (
                    <Button
                      key={sample.name}
                      type="button"
                      variant="outline"
                      size="sm"
                      className="border-zinc-700 text-zinc-300 hover:bg-zinc-800"
                      onClick={() => applySamplePrompt(sample)}
                    >
                      {sample.name}
                    </Button>
                  ))}
                </div>
              </div>
            )}

            {/* Name */}
            <div className="space-y-2">
              <Label htmlFor="name">{t('name')} *</Label>
              <Input
                id="name"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                placeholder={t('name')}
                className="bg-zinc-800 border-zinc-700"
                maxLength={30}
                required
              />
              <p className="text-xs text-zinc-500">{formData.name.length}/30</p>
            </div>

            {/* Description */}
            <div className="space-y-2">
              <Label htmlFor="description">{t('profile')} *</Label>
              <Input
                id="description"
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                placeholder={t('profile')}
                className="bg-zinc-800 border-zinc-700"
                maxLength={100}
                required
              />
              <p className="text-xs text-zinc-500">{formData.description.length}/100</p>
            </div>

            {/* Gender & Voice */}
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>{t('assistant_gender')}</Label>
                <Select value={formData.gender} onValueChange={(v) => setFormData({ ...formData, gender: v })}>
                  <SelectTrigger className="bg-zinc-800 border-zinc-700">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-zinc-800 border-zinc-700">
                    {GENDER_OPTIONS.map((opt) => (
                      <SelectItem key={opt.value} value={opt.value}>{t(opt.labelKey) || opt.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>{t('assistant_voice')}</Label>
                <Select value={formData.voice} onValueChange={(v) => setFormData({ ...formData, voice: v })}>
                  <SelectTrigger className="bg-zinc-800 border-zinc-700">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-zinc-800 border-zinc-700">
                    {VOICE_OPTIONS.map((opt) => (
                      <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            {/* Personality Style */}
            <div className="space-y-2">
              <Label>{t('assistant_communication_style')} *</Label>
              <div className="grid grid-cols-2 gap-2">
                {PERSONALITY_STYLES.map((style) => (
                  <button
                    key={style.value}
                    type="button"
                    onClick={() => setFormData({ ...formData, personality_style: style.value })}
                    className={`p-3 rounded-lg border text-left transition-all ${
                      formData.personality_style === style.value
                        ? "border-purple-500 bg-purple-500/20"
                        : "border-zinc-700 bg-zinc-800/50 hover:bg-zinc-800"
                    }`}
                  >
                    <div className="font-medium text-sm">{t(style.labelKey) || style.label}</div>
                    <div className="text-xs text-zinc-500 mt-1">{t(style.descKey) || style.description}</div>
                  </button>
                ))}
              </div>
            </div>

            {/* System Prompt */}
            <div className="space-y-2">
              <Label htmlFor="system_prompt">{t('assistant_personality_def')} *</Label>
              <Textarea
                id="system_prompt"
                value={formData.system_prompt}
                onChange={(e) => setFormData({ ...formData, system_prompt: e.target.value })}
                placeholder={t('assistant_prompt_placeholder')}
                className="bg-zinc-800 border-zinc-700 min-h-[120px]"
                maxLength={1000}
                required
              />
              <p className="text-xs text-zinc-500">{formData.system_prompt.length}/1000 {t('characters')}</p>
            </div>

            {/* Preview */}
            <div className="p-4 bg-zinc-800/50 rounded-lg border border-zinc-700">
              <h4 className="text-sm font-medium mb-2">{t('profile')}</h4>
              <div className="flex items-center gap-3">
                <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${
                  formData.gender === "male" ? "bg-blue-600" :
                  formData.gender === "female" ? "bg-pink-600" : "bg-purple-600"
                }`}>
                  <GenderIcon size={24} className="text-white" />
                </div>
                <div>
                  <h5 className="font-medium">{formData.name || t('name')}</h5>
                  <p className="text-xs text-zinc-500">{formData.description || t('profile')}</p>
                </div>
              </div>
            </div>

            {/* Actions */}
            <div className="flex gap-2 pt-2">
              <Button
                type="button"
                variant="outline"
                className="flex-1 border-zinc-700"
                onClick={resetForm}
              >
                {t('cancel')}
              </Button>
              <Button
                type="submit"
                disabled={isCreating || !formData.name || !formData.description || !formData.system_prompt}
                className="flex-1 bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700"
              >
                {isCreating ? (
                  <Loader2 className="animate-spin mr-2" size={18} />
                ) : editingAssistant ? (
                  t('save')
                ) : (
                  t('add')
                )}
              </Button>
            </div>
          </form>
        )}
      </DialogContent>
    </Dialog>
  );
}

export default AssistantCreatorModal;
